import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError, API, BACKEND_URL } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { FileText, Download, Sparkles, Wand2, Loader2, Users, Plus, Trash2 } from "lucide-react";

// Campos de "corpo livre" elegíveis para geração automática por IA
const AI_TARGET_FIELDS = new Set([
  "corpo",
  "pauta",
  "objetivo",
  "fundamentacao",
  "pedido",
  "finalidade",
  "objeto",
]);

// Campo que dispara a geração automática (ao perder o foco)
const AUTO_TRIGGER_FIELD = "assunto";

export default function DocumentTemplatesManager() {
  const [info, setInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [values, setValues] = useState({});
  const [generating, setGenerating] = useState(false);
  const [aiLoading, setAiLoading] = useState(null); // key do campo em geração
  const [refiningAssunto, setRefiningAssunto] = useState(false);
  const [cooperados, setCooperados] = useState([]);
  const autoFiredRef = useRef(false);

  useEffect(() => {
    api.get("/secretariat/templates")
      .then((r) => { setInfo(r.data.info); setTemplates(r.data.templates); })
      .catch((e) => toast.error(formatApiError(e)));
    // Carrega cooperados para o seletor (usado por modelos como Termo de Acordo)
    api.get("/secretariat/cooperados")
      .then((r) => setCooperados(r.data || []))
      .catch(() => setCooperados([]));
  }, []);

  const open = (tpl) => {
    setActive(tpl);
    const initial = {};
    tpl.fields.forEach((f) => {
      if (f.type === "payments_list") {
        initial[f.key] = [
          { mes: "", valor_num: "", valor_extenso: "", status: "efetuado", data_efetivada: "" },
        ];
      } else if (f.type === "checkbox") {
        initial[f.key] = false;
      } else {
        initial[f.key] = "";
      }
    });
    setValues(initial);
    autoFiredRef.current = false;
  };

  const close = () => {
    setActive(null);
    setValues({});
    setAiLoading(null);
    setRefiningAssunto(false);
    autoFiredRef.current = false;
  };

  const pickPrimaryAiField = (tpl) => {
    // Encontra o melhor campo "corpo" do modelo atual para auto-preenchimento
    const f = tpl.fields.find((x) => AI_TARGET_FIELDS.has(x.key));
    return f ? f.key : null;
  };

  const refineAssunto = async (raw) => {
    // Chama IA para corrigir ortografia/acentuação do assunto
    const token = localStorage.getItem("coomflona_token");
    const res = await fetch(`${API}/secretariat/templates/ai-refine-assunto`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ template_id: active?.id || "", assunto: raw }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({ detail: "Erro" }));
      throw new Error(j.detail || "Erro ao revisar assunto");
    }
    const data = await res.json();
    return (data.assunto || raw).trim();
  };

  const callAi = async (fieldKey, { silent = false } = {}) => {
    if (!active) return;
    const assunto = (values.assunto || "").trim();
    if (!assunto) {
      if (!silent) toast.error("Preencha o Assunto antes de gerar com IA.");
      return;
    }
    setAiLoading(fieldKey);
    try {
      const token = localStorage.getItem("coomflona_token");
      // Serializa contexto já preenchido (exceto o próprio campo) para enriquecer a IA
      const contextoLines = Object.entries(values)
        .filter(([k, v]) => k !== fieldKey && (v || "").toString().trim())
        .map(([k, v]) => {
          const label = (active.fields.find((f) => f.key === k) || {}).label || k;
          return `${label}: ${v}`;
        });
      const res = await fetch(`${API}/secretariat/templates/ai-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          template_id: active.id,
          assunto,
          contexto: contextoLines.join("\n"),
          field_key: fieldKey,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({ detail: "Erro" }));
        throw new Error(j.detail || "Erro ao gerar com IA");
      }
      const data = await res.json();
      setValues((prev) => ({ ...prev, [fieldKey]: data.text || "" }));
      if (!silent) toast.success("Texto gerado pela IA!");
    } catch (err) {
      if (!silent) toast.error(err.message || "Erro inesperado");
    } finally {
      setAiLoading(null);
    }
  };

  const handleAssuntoBlur = async () => {
    if (!active) return;
    if (autoFiredRef.current) return;
    const assunto = (values.assunto || "").trim();
    if (assunto.length < 5) return; // evita disparos com poucas letras
    autoFiredRef.current = true;

    // 1) Primeiro tenta corrigir ortografia/acentuação do próprio assunto
    let finalAssunto = assunto;
    try {
      setRefiningAssunto(true);
      const corrected = await refineAssunto(assunto);
      if (corrected && corrected !== assunto) {
        finalAssunto = corrected;
        setValues((prev) => ({ ...prev, assunto: corrected }));
        toast.success("Assunto revisado pela IA (ortografia/acentuação).");
      }
    } catch (err) {
      // silencioso: se falhar a revisão, mantém o assunto original
      console.warn("Falha ao revisar assunto:", err.message);
    } finally {
      setRefiningAssunto(false);
    }

    // 2) Em seguida, gera automaticamente o campo de corpo (se houver)
    const targetKey = pickPrimaryAiField(active);
    if (!targetKey) return;
    if ((values[targetKey] || "").trim()) return; // não sobrescreve texto do usuário
    // usa o assunto corrigido para a geração
    setValues((prev) => ({ ...prev, assunto: finalAssunto }));
    setTimeout(() => callAi(targetKey, { silent: false }), 50);
  };

  const [generatedResult, setGeneratedResult] = useState(null); // {protocol, doc_id, download_url, cooperado_name, cooperado_phone, template_title}

  const generate = async () => {
    if (!active) return;
    setGenerating(true);
    try {
      const { data } = await api.post(`/secretariat/templates/generate`, {
        template_id: active.id,
        data: values,
        return_json: true,
      });
      // Baixa o PDF na hora
      const publicUrl = `${BACKEND_URL}${data.download_url}`;
      try {
        const pdfRes = await fetch(publicUrl);
        const blob = await pdfRes.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${data.protocol}_${active.id}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      } catch (err) {
        // Se falhar o download automático, o usuário ainda pode baixar pelo botão
      }
      toast.success(`Documento gerado: ${data.protocol}`);
      setGeneratedResult({ ...data, public_url: publicUrl });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mt-8" data-testid="templates-manager">
      <div className="bg-forest-700 text-cream-100 p-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-copaiba-300" />
          <div className="font-display text-xl">Modelos de Documentos Oficiais</div>
        </div>
        <p className="text-cream-100/85 text-sm leading-relaxed">
          Escolha um modelo, preencha os campos e gere o PDF pronto com o cabeçalho oficial da COOMFLONA — CNPJ
          {info ? ` ${info.cnpj}` : ""}, IE {info ? info.ie : ""}, {info ? info.address : ""}. Os documentos saem prontos
          para imprimir, assinar e protocolar. Agora com <b>geração automática de texto por IA</b>: basta digitar o assunto.
        </p>
      </div>

      <SignaturePanel />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {templates.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => open(t)}
            data-testid={`template-card-${t.id}`}
            className="text-left bg-white border border-cream-400 p-6 hover:border-forest-700 hover:bg-cream-200 transition-all duration-300 group"
          >
            <FileText className="w-6 h-6 text-copaiba-700 mb-4" />
            <div className="font-display text-xl text-forest-900 leading-tight">{t.title}</div>
            <p className="text-sm text-forest-900/75 mt-2 leading-relaxed">{t.description}</p>
            <div className="mt-4 text-xs uppercase tracking-[0.18em] text-forest-700 group-hover:text-copaiba-700">
              Preencher e gerar PDF →
            </div>
          </button>
        ))}
        {templates.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 bg-cream-200 border border-cream-400 p-10 text-center text-forest-900/65">
            Carregando modelos...
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{active.title}</DialogTitle>
                <p className="text-sm text-forest-900/70 mt-1">{active.description}</p>
              </DialogHeader>

              {active.fields.some((f) => f.key === AUTO_TRIGGER_FIELD) && (
                <div className="mt-3 bg-copaiba-100 border border-copaiba-300 text-forest-900 text-xs p-3 flex items-start gap-2" data-testid="ai-hint">
                  <Wand2 className="w-4 h-4 text-copaiba-700 mt-[2px] flex-shrink-0" />
                  <span>
                    Dica: preencha o <b>Assunto</b> e a IA escreverá o texto automaticamente. Você também pode clicar em
                    <b> ✨ Gerar com IA</b> em campos longos.
                  </span>
                </div>
              )}

              <div className="space-y-4 mt-3">
                {active.fields.map((f) => {
                  const isAiTarget = AI_TARGET_FIELDS.has(f.key);
                  const isAuto = f.key === AUTO_TRIGGER_FIELD;
                  const fieldLoading = aiLoading === f.key;
                  return (
                    <div key={f.key}>
                      <div className="flex items-center justify-between gap-2">
                        <Label>{f.label}{f.required && <span className="text-terracotta-500"> *</span>}</Label>
                        {isAuto && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={refiningAssunto || !!aiLoading}
                            onClick={async () => {
                              const raw = (values.assunto || "").trim();
                              if (!raw) return toast.error("Digite o assunto antes.");
                              try {
                                setRefiningAssunto(true);
                                const corrected = await refineAssunto(raw);
                                if (corrected && corrected !== raw) {
                                  setValues((prev) => ({ ...prev, assunto: corrected }));
                                  toast.success("Assunto revisado pela IA.");
                                } else {
                                  toast.success("Assunto já está correto!");
                                }
                              } catch (err) {
                                toast.error(err.message || "Erro ao revisar assunto.");
                              } finally {
                                setRefiningAssunto(false);
                              }
                            }}
                            data-testid="tpl-ai-refine-assunto"
                            className="h-7 px-2 text-xs text-copaiba-700 hover:text-copaiba-700 hover:bg-copaiba-100 rounded-none"
                          >
                            {refiningAssunto ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Revisando...</>
                            ) : (
                              <><Wand2 className="w-3 h-3 mr-1" /> Corrigir com IA</>
                            )}
                          </Button>
                        )}
                        {isAiTarget && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => callAi(f.key)}
                            disabled={fieldLoading || !!aiLoading}
                            data-testid={`tpl-ai-${f.key}`}
                            className="h-7 px-2 text-xs text-copaiba-700 hover:text-copaiba-700 hover:bg-copaiba-100 rounded-none"
                          >
                            {fieldLoading ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Gerando...</>
                            ) : (
                              <><Wand2 className="w-3 h-3 mr-1" /> Gerar com IA</>
                            )}
                          </Button>
                        )}
                      </div>
                      {f.type === "payments_list" ? (
                        <div className="space-y-3 border border-cream-400 bg-cream-100 p-3" data-testid={`tpl-payments-${f.key}`}>
                          {(values[f.key] || []).map((p, idx) => (
                            <div key={idx} className="bg-white border border-cream-400 p-3 space-y-2" data-testid={`payment-row-${idx}`}>
                              <div className="flex items-center justify-between">
                                <div className="text-xs uppercase tracking-[0.18em] text-forest-700 font-medium">
                                  Pagamento #{idx + 1}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const arr = [...(values[f.key] || [])];
                                    arr.splice(idx, 1);
                                    setValues({ ...values, [f.key]: arr });
                                  }}
                                  data-testid={`payment-remove-${idx}`}
                                  className="text-terracotta-500 hover:text-terracotta-700 p-1"
                                  title="Remover este pagamento"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs">Mês/ano</Label>
                                  <Input
                                    placeholder="Ex.: Abril de 2026"
                                    value={p.mes || ""}
                                    onChange={(e) => {
                                      const arr = [...(values[f.key] || [])];
                                      arr[idx] = { ...arr[idx], mes: e.target.value };
                                      setValues({ ...values, [f.key]: arr });
                                    }}
                                    data-testid={`payment-mes-${idx}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Status</Label>
                                  <Select
                                    value={p.status || "efetuado"}
                                    onValueChange={(v) => {
                                      const arr = [...(values[f.key] || [])];
                                      arr[idx] = { ...arr[idx], status: v };
                                      setValues({ ...values, [f.key]: arr });
                                    }}
                                  >
                                    <SelectTrigger className="rounded-none" data-testid={`payment-status-${idx}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="efetuado">Já efetuado (Cláusula 2ª)</SelectItem>
                                      <SelectItem value="pendente">Pendente (Cláusula 3ª)</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Valor (R$)</Label>
                                  <Input
                                    placeholder="Ex.: 1.700,00"
                                    value={p.valor_num || ""}
                                    onChange={(e) => {
                                      const arr = [...(values[f.key] || [])];
                                      arr[idx] = { ...arr[idx], valor_num: e.target.value };
                                      setValues({ ...values, [f.key]: arr });
                                    }}
                                    data-testid={`payment-valor-${idx}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Valor por extenso</Label>
                                  <Input
                                    placeholder="Ex.: mil e setecentos reais"
                                    value={p.valor_extenso || ""}
                                    onChange={(e) => {
                                      const arr = [...(values[f.key] || [])];
                                      arr[idx] = { ...arr[idx], valor_extenso: e.target.value };
                                      setValues({ ...values, [f.key]: arr });
                                    }}
                                    data-testid={`payment-extenso-${idx}`}
                                  />
                                </div>
                                {(p.status || "efetuado") === "efetuado" && (
                                  <div className="sm:col-span-2">
                                    <Label className="text-xs">Data em que foi efetuado (opcional)</Label>
                                    <Input
                                      placeholder="Ex.: 15 de julho de 2026"
                                      value={p.data_efetivada || ""}
                                      onChange={(e) => {
                                        const arr = [...(values[f.key] || [])];
                                        arr[idx] = { ...arr[idx], data_efetivada: e.target.value };
                                        setValues({ ...values, [f.key]: arr });
                                      }}
                                      data-testid={`payment-data-${idx}`}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const arr = [...(values[f.key] || [])];
                              arr.push({ mes: "", valor_num: "", valor_extenso: "", status: "efetuado", data_efetivada: "" });
                              setValues({ ...values, [f.key]: arr });
                            }}
                            data-testid={`payment-add-${f.key}`}
                            className="w-full rounded-none border-dashed border-forest-700 text-forest-900 hover:bg-cream-200"
                          >
                            <Plus className="w-4 h-4 mr-2" /> Adicionar mês / pagamento
                          </Button>
                          <p className="text-xs text-forest-900/60">
                            Cada linha vira um item da Cláusula Segunda (efetuados) ou Terceira (pendentes) do termo.
                          </p>
                        </div>
                      ) : f.type === "cooperado_picker" ? (
                        <Select
                          value={values[f.key] || ""}
                          onValueChange={(v) => {
                            const c = cooperados.find((x) => x.id === v);
                            if (c) {
                              setValues((prev) => ({
                                ...prev,
                                [f.key]: v,
                                cooperado_nome: c.name || prev.cooperado_nome || "",
                                cooperado_comunidade: c.community || prev.cooperado_comunidade || "",
                              }));
                              toast.success(`Cooperado "${c.name}" selecionado. Complete CPF e RG.`);
                            } else {
                              setValues((prev) => ({ ...prev, [f.key]: v }));
                            }
                          }}
                        >
                          <SelectTrigger data-testid={`tpl-picker-${f.key}`} className="rounded-none">
                            <SelectValue placeholder={cooperados.length ? `Buscar entre ${cooperados.length} cooperados...` : "Nenhum cooperado cadastrado"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {cooperados.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name}{c.community ? ` — ${c.community}` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : f.type === "textarea" ? (
                        <Textarea
                          rows={f.rows || 4}
                          placeholder={f.placeholder || ""}
                          value={values[f.key] || ""}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          data-testid={`tpl-field-${f.key}`}
                          disabled={fieldLoading}
                        />
                      ) : f.type === "checkbox" ? (
                        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={`tpl-field-${f.key}`}>
                          <input
                            type="checkbox"
                            checked={!!values[f.key]}
                            onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })}
                            className="w-4 h-4 accent-forest-700"
                          />
                          <span className="text-sm text-forest-900">Marcar como anexado</span>
                        </label>
                      ) : f.type === "select" ? (
                        <Select
                          value={values[f.key] || ""}
                          onValueChange={(v) => setValues({ ...values, [f.key]: v })}
                        >
                          <SelectTrigger data-testid={`tpl-field-${f.key}`} className="rounded-none">
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(f.options || []).map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          type="text"
                          placeholder={f.placeholder || ""}
                          value={values[f.key] || ""}
                          onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                          onBlur={isAuto ? handleAssuntoBlur : undefined}
                          data-testid={`tpl-field-${f.key}`}
                          disabled={(fieldLoading && isAuto) || (isAuto && refiningAssunto)}
                        />
                      )}
                      {f.type === "cooperado_picker" && (
                        <p className="mt-1 text-xs text-forest-900/60 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          Selecionar um cooperado preenche automaticamente o Nome e a Comunidade abaixo. CPF e RG precisam ser preenchidos manualmente.
                        </p>
                      )}
                      {isAuto && refiningAssunto && (
                        <div className="mt-1 text-xs text-copaiba-700 flex items-center gap-1" data-testid="assunto-refining">
                          <Loader2 className="w-3 h-3 animate-spin" /> Revisando ortografia e acentuação...
                        </div>
                      )}
                      {isAuto && !refiningAssunto && aiLoading && (
                        <div className="mt-1 text-xs text-copaiba-700 flex items-center gap-1" data-testid="ai-auto-status">
                          <Loader2 className="w-3 h-3 animate-spin" /> Gerando texto automaticamente...
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={close} className="rounded-none">Cancelar</Button>
                <Button
                  type="button"
                  onClick={generate}
                  disabled={generating || !!aiLoading}
                  data-testid="tpl-generate"
                  className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none"
                >
                  {generating ? "Gerando..." : (<><Download className="w-4 h-4 mr-2" /> Gerar PDF</>)}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <GeneratedDocDialog result={generatedResult} onClose={() => setGeneratedResult(null)} />
    </div>
  );
}


// -------- Dialog: resultado da geração (protocolo + WhatsApp) --------
function GeneratedDocDialog({ result, onClose }) {
  const [phoneOverride, setPhoneOverride] = useState("");

  useEffect(() => {
    setPhoneOverride(result?.cooperado_phone || "");
  }, [result]);

  if (!result) return null;

  const publicUrl = result.public_url || `${BACKEND_URL}${result.download_url}`;

  const normalizedPhone = (phoneOverride || "").replace(/\D/g, "");
  const waPhone = normalizedPhone.startsWith("55") ? normalizedPhone : `55${normalizedPhone}`;
  const message = `*COOMFLONA — Documento Oficial*\n\nOlá${result.cooperado_name ? ", " + result.cooperado_name : ""}!\n\nSegue documento emitido pela Secretaria da COOMFLONA:\n\n• *Tipo:* ${result.template_title}\n• *Protocolo:* ${result.protocol}\n\nBaixe o PDF oficial em:\n${publicUrl}\n\nCooperativa Mista da Flona do Tapajós.`;
  const waLink = `https://wa.me/${waPhone}?text=${encodeURIComponent(message)}`;

  return (
    <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="generated-doc-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest-900">Documento arquivado ✓</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-cream-100 border border-cream-400 p-4">
            <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-1">Número de protocolo</div>
            <div className="font-mono text-2xl font-bold text-forest-900 tracking-wider" data-testid="generated-doc-protocol">
              {result.protocol}
            </div>
            <div className="text-xs text-forest-900/60 mt-2">
              {result.template_title}{result.cooperado_name ? ` — ${result.cooperado_name}` : ""}
            </div>
          </div>

          <div className="border border-cream-400 p-4">
            <Label className="text-xs uppercase tracking-[0.15em] text-forest-700">Enviar por WhatsApp</Label>
            <Input
              type="text"
              placeholder="Número (com DDD, ex.: 93991177997)"
              value={phoneOverride}
              onChange={(e) => setPhoneOverride(e.target.value)}
              data-testid="generated-doc-phone-input"
              className="mt-2"
            />
            <p className="text-xs text-forest-900/60 mt-2 leading-relaxed">
              A mensagem já vem pronta com o link do PDF. O destinatário toca no link e baixa o documento oficial
              direto do arquivo da COOMFLONA.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="generated-doc-download"
              className="btn-primary flex-1 justify-center bg-forest-700 text-cream-100 hover:bg-forest-900"
            >
              Baixar PDF novamente
            </a>
            <a
              href={normalizedPhone ? waLink : "#"}
              target={normalizedPhone ? "_blank" : "_self"}
              rel="noreferrer"
              onClick={(e) => { if (!normalizedPhone) { e.preventDefault(); toast.error("Informe o número de WhatsApp do destinatário."); } }}
              data-testid="generated-doc-whatsapp"
              className={`btn-primary flex-1 justify-center inline-flex items-center gap-2 ${normalizedPhone ? "bg-[#25D366] text-white hover:opacity-90" : "bg-cream-400 text-forest-900/50 cursor-not-allowed"}`}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.67 4.07 1.83 5.68L2 22l4.55-1.94c1.55.85 3.32 1.34 5.49 1.34 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.79 14.03c-.24.68-1.42 1.31-1.96 1.35-.5.05-1.14.06-1.83-.12-.42-.11-.96-.29-1.66-.6-2.94-1.27-4.86-4.24-5.01-4.43-.14-.19-1.19-1.58-1.19-3.03 0-1.44.76-2.15 1.03-2.44.27-.29.59-.36.79-.36h.56c.18 0 .43-.07.66.5.24.6.83 2.05.9 2.2.07.14.12.32.02.51-.1.19-.15.31-.29.48-.15.17-.31.38-.44.51-.14.14-.29.29-.13.57.17.29.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.29.14.46.12.63-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.27.1 1.71.8 2 .95.29.14.48.22.55.34.07.12.07.69-.17 1.37Z"/></svg>
              Enviar por WhatsApp
            </a>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="generated-doc-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------- Painel: minha assinatura digital --------
function SignaturePanel() {
  const [me, setMe] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);

  const reload = async () => {
    try {
      const { data } = await api.get("/secretariat/me");
      setMe(data);
      setCacheBust(Date.now());
    } catch (err) {
      // silencioso: usuário verá painel padrão
      setMe({ has_signature: false });
    }
  };

  useEffect(() => { reload(); }, []);

  const handleUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie uma imagem (PNG recomendado)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      await api.post("/secretariat/me/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Assinatura salva! Aparecerá automaticamente nos próximos documentos.");
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remover sua assinatura digital? Você poderá cadastrar outra depois.")) return;
    try {
      await api.delete("/secretariat/me/signature");
      toast.success("Assinatura removida.");
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sigSrc = me?.has_signature ? `${BACKEND_URL}${me.signature_url}&_=${cacheBust}` : "";

  return (
    <div className="bg-cream-100 border border-cream-400 p-5 mb-6" data-testid="signature-panel">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.18em] text-forest-700 mb-1">Minha assinatura digital</div>
          <div className="font-display text-lg text-forest-900">
            {me?.has_signature ? "Assinatura ativa" : "Nenhuma assinatura cadastrada"}
          </div>
          <p className="text-sm text-forest-900/70 mt-2 leading-relaxed">
            Quando cadastrada, sua assinatura é impressa <b>automaticamente acima da linha</b> nos documentos que você
            gerar (guias de encaminhamento, ofícios, encaminhamentos etc.). Recomendamos <b>PNG com fundo transparente</b>,
            largura de 400–800px, peso máximo 2MB.
          </p>
        </div>
        <div className="flex flex-col items-start gap-2">
          {me?.has_signature ? (
            <div className="border border-cream-400 bg-white p-2 min-w-[200px]">
              <img src={sigSrc} alt="Sua assinatura" className="max-h-16 max-w-[220px] object-contain" data-testid="signature-preview" />
            </div>
          ) : (
            <div className="border border-dashed border-forest-700/40 bg-white/50 min-w-[200px] h-20 flex items-center justify-center text-xs text-forest-900/50">
              (sem assinatura)
            </div>
          )}
          <div className="flex gap-2">
            <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-forest-700 text-forest-900 hover:bg-cream-200 text-xs">
              {uploading ? "Enviando..." : (me?.has_signature ? "Trocar imagem" : "+ Enviar assinatura")}
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="signature-upload" />
            </label>
            {me?.has_signature && (
              <button type="button" onClick={handleRemove} className="text-xs text-terracotta-500 hover:text-terracotta-700 px-2" data-testid="signature-remove">
                Remover
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
